import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Trophy } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import UserTrophiesModal from "./UserTrophiesModal";
import { isAdminPosition } from "../../access/positions";
import { useWarehouseScope } from "../../access/useWarehouseScope";
import "../../styles/admin.css";
import "../../styles/logs.css";
import "../../styles/points-admin.css";

export const RANGES = [
    "today",
    "yesterday",
    "this_week",
    "last_week",
    "this_month",
    "last_month",
    "this_year",
    "all",
    "custom",
] as const;

export type PointsRange = typeof RANGES[number];

export interface LeaderboardRow {
    rank: number;
    uid: number;
    name: string | null;
    warehouse: string | null;
    /** False for rows banked before points carried a site (migration 157). */
    warehouse_known: boolean;
    score: number;
    days: number;
    best_day: number;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Points are seconds saved. Reading "5,412" tells a manager less than
 * "1h 30m", so show both: the score is the currency, the duration is what it
 * means.
 */
export const formatSaved = (seconds: number): string => {
    if (!Number.isFinite(seconds) || seconds <= 0) return "0m";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    if (hours === 0) return `${mins}m`;
    return `${hours}h ${String(mins).padStart(2, "0")}m`;
};

/**
 * Warehouse > Points > Leaderboard.
 *
 * Ranges are named, not dated: the server resolves them on the London calendar
 * that points days are stamped in. Deriving "this week" here with the browser's
 * clock would disagree with the data for the last hour of every summer evening,
 * so the FE sends `range=this_week` and renders back whatever window the server
 * says it used.
 */
// Same shape as DashboardAdmin's sessionUser: Admins start unfiltered,
// everyone else starts on their own site. `permissions` here is whatever
// isAdminPosition() can read — the position field when present, else the
// (possibly legacy) permissions string.
const sessionUser = (): { permissions: string | null; warehouse: string | null } => {
    try {
        const raw = window.localStorage.getItem("user");
        if (raw) {
            const u = JSON.parse(raw);
            return { permissions: u.position ?? u.permissions ?? null, warehouse: u.warehouse ?? null };
        }
    } catch { /* fallthrough */ }
    return { permissions: null, warehouse: null };
};

const PointsLeaderboard = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState<LeaderboardRow[]>([]);
    // Today by default (per Jack, 2026-08-26): the weekly view read as a
    // daily number once too often — a "15k day" turned out to be a
    // week-to-date total. The range picker still offers every window.
    const [range, setRange] = useState<PointsRange>("today");
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    // Admins land on their own site's board; superAdmins on every site
    // (mirrors the admin dashboard's scoping). Only the DEFAULT differs —
    // the dropdown stays live for everyone, and the unstamped-rows note
    // below still explains what a site filter cannot show.
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouse] = useState<string>(() => {
        const user = sessionUser();
        return isAdminPosition(user.permissions) ? "" : (user.warehouse ?? "");
    });
    // Without cross_warehouse the board is pinned to the person's own warehouse.
    const warehouse = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    const [window_, setWindow] = useState<{ from: string | null; to: string | null }>({
        from: null, to: null,
    });
    // Operator-days in range with no site recorded. A warehouse filter can
    // never match them, so the board has to account for them out loud.
    const [unstamped, setUnstamped] = useState(0);
    const [loading, setLoading] = useState(true);
    // Click a row → that operator's trophy cabinet.
    const [selected, setSelected] = useState<{ uid: number; name: string | null } | null>(null);

    // Same source as every other admin warehouse picker, so a hidden
    // warehouse disappears from all of them at once.
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                if (alive) setWarehouses(data.map(w => w.warehouse).sort());
            } catch {
                // Leave empty — the All option still works.
            }
        })();
        return () => { alive = false; };
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ range });
            if (warehouse) params.set("warehouse", warehouse);
            if (range === "custom") {
                if (DATE_RE.test(from)) params.set("from", from);
                if (DATE_RE.test(to)) params.set("to", to);
            }
            const res = await apiFetch(`/admin/points/leaderboard?${params.toString()}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setRows(Array.isArray(data?.rows) ? data.rows : []);
            setWindow({ from: data?.from ?? null, to: data?.to ?? null });
            setUnstamped(Number(data?.unstamped) || 0);
        } catch {
            setRows([]);
            setUnstamped(0);
            toast.error(t("server_error"), { toastId: "points-leaderboard-err" });
        } finally {
            setLoading(false);
        }
    }, [range, warehouse, from, to, t]);

    useEffect(() => { void load(); }, [load]);

    const topScore = rows.length > 0 ? rows[0].score : 0;

    return (
        <div className="logs-admin users-admin">
            <div className="users-admin-filters">
                <select
                    className="locations-table-search"
                    value={range}
                    onChange={e => setRange(e.target.value as PointsRange)}
                    aria-label={t("points_range")}
                >
                    {RANGES.map(r => (
                        <option key={r} value={r}>{t(`points_range_${r}`)}</option>
                    ))}
                </select>

                {range === "custom" && (
                    <>
                        <input
                            className="locations-table-search logs-filter-date"
                            type="date"
                            value={from}
                            onChange={e => setFrom(e.target.value)}
                            aria-label={t("logs_filter_from")}
                        />
                        <input
                            className="locations-table-search logs-filter-date"
                            type="date"
                            value={to}
                            onChange={e => setTo(e.target.value)}
                            aria-label={t("logs_filter_to")}
                        />
                    </>
                )}

                <select
                    className="locations-table-search logs-filter-warehouse"
                    value={warehouse}
                    onChange={e => setWarehouse(e.target.value)}
                    disabled={!warehouseScope.canCross}
                    aria-label={t("logs_col_warehouse")}
                >
                    {warehouseScope.canCross && <option value="">{t("logs_filter_warehouse_all")}</option>}
                    {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                </select>

                <button
                    type="button"
                    className="logs-action-btn"
                    onClick={() => void load()}
                    aria-label={t("refresh")}
                    title={t("refresh")}
                >
                    <RefreshCw size={18} />
                </button>
            </div>

            {/* The window the SERVER used, not one derived here. */}
            {window_.from && window_.to && (
                <p className="admin-settings-section-hint">
                    {window_.from === window_.to
                        ? window_.from
                        : `${window_.from} → ${window_.to}`}
                    {" · "}
                    {t("points_leaderboard_lag")}
                </p>
            )}

            {/* A site filter cannot match rows that carry no site, so say so
                rather than showing a shorter board with no explanation. */}
            {!loading && warehouse !== "" && unstamped > 0 && (
                <p className="admin-settings-section-hint points-unstamped-note">
                    {t("points_unstamped_hidden", { count: String(unstamped) })}
                </p>
            )}

            {loading ? (
                <div className="locations-table-empty">{t("loading")}</div>
            ) : rows.length === 0 ? (
                <div className="locations-table-empty">{t("points_no_scores")}</div>
            ) : (
                <div className="locations-table-wrap">
                    <table className="locations-table">
                        <thead>
                            <tr>
                                <th>{t("points_col_rank")}</th>
                                <th>{t("points_col_name")}</th>
                                <th>{t("logs_col_warehouse")}</th>
                                <th>{t("score_label")}</th>
                                <th>{t("points_col_time_saved")}</th>
                                <th>{t("points_col_days")}</th>
                                <th>{t("points_col_best_day")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr
                                    key={row.uid}
                                    className="points-user-row"
                                    onClick={() => setSelected({ uid: row.uid, name: row.name })}
                                >
                                    <td>
                                        {row.rank <= 3
                                            ? <Trophy size={14} className={`points-rank-icon points-rank-${row.rank}`} />
                                            : null}
                                        {" "}{row.rank}
                                    </td>
                                    <td>{row.name ?? `#${row.uid}`}</td>
                                    <td>
                                        {row.warehouse ?? "—"}
                                        {/* Inferred from the user's current site
                                            because the row predates the stamp. */}
                                        {row.warehouse && !row.warehouse_known && " *"}
                                    </td>
                                    <td>
                                        <div className="points-score-cell">
                                            <span className="points-score-value">
                                                {row.score.toLocaleString()}
                                            </span>
                                            <span
                                                className="points-score-bar"
                                                style={{
                                                    width: topScore > 0
                                                        ? `${Math.max(4, (row.score / topScore) * 100)}%`
                                                        : "0%",
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td>{formatSaved(row.score)}</td>
                                    <td>{row.days}</td>
                                    <td>{row.best_day.toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <UserTrophiesModal
                    uid={selected.uid}
                    name={selected.name}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
};

export default PointsLeaderboard;
